import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import EmptyState from './EmptyState';

export default function DataTable({
  columns,
  data = [],
  loading = false,
  searchKey,
  searchPlaceholder = 'Search records...',
  filters = [],
  actions,
  emptyIcon,
  emptyTitle = 'No records found',
  emptyDescription = 'There are no records to display.',
  onAddNew,
  addNewLabel,
  itemsPerPage = 10,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // Filter & Search Logic
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // 1. Search filter
      if (searchTerm && searchKey) {
        const valueToSearch = typeof searchKey === 'function' ? searchKey(item) : item[searchKey];
        if (!String(valueToSearch || '').toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }
      }

      // 2. Dropdown filters
      for (const filter of filters) {
        const selectedValue = filterValues[filter.key];
        if (selectedValue && selectedValue !== 'ALL') {
          if (String(item[filter.key]) !== String(selectedValue)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [data, searchTerm, searchKey, filterValues, filters]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Top Bar: Search + Filters + Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 flex-1">
          {searchKey && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#9BB7A3] focus:ring-1 focus:ring-[#9BB7A3]"
              />
            </div>
          )}

          {filters.map((filter) => (
            <select
              key={filter.key}
              value={filterValues[filter.key] || 'ALL'}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-300 focus:outline-none focus:border-[#9BB7A3]"
            >
              <option value="ALL">{filter.label}: All</option>
              {filter.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ))}
        </div>

        {onAddNew && addNewLabel && (
          <button
            onClick={onAddNew}
            className="px-4 py-2.5 bg-[#9BB7A3] hover:bg-[#8aa892] text-slate-950 font-bold text-xs sm:text-sm rounded-xl transition shadow-sm active:scale-95 whitespace-nowrap self-end sm:self-auto"
          >
            + {addNewLabel}
          </button>
        )}
      </div>

      {/* Table Container */}
      <div className="bg-slate-900/90 border border-slate-800/90 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs sm:text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/80 text-slate-400 uppercase text-[11px] font-bold tracking-wider">
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className={`px-5 py-4 whitespace-nowrap ${col.className || ''}`}
                  >
                    {col.header}
                  </th>
                ))}
                {actions && (
                  <th className="px-5 py-4 text-right whitespace-nowrap">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={columns.length + (actions ? 1 : 0)} className="py-16 text-center">
                    <div className="w-8 h-8 border-2 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-xs text-slate-400 font-medium">Loading database records...</p>
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row, rowIndex) => (
                  <tr
                    key={row.id || rowIndex}
                    className="hover:bg-slate-800/40 transition-colors"
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-5 py-3.5 align-middle ${col.className || ''}`}
                      >
                        {col.accessor ? (typeof col.accessor === 'function' ? col.accessor(row, rowIndex) : row[col.accessor]) : null}
                      </td>
                    ))}
                    {actions && (
                      <td className="px-5 py-3.5 text-right align-middle whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          {actions(row)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (actions ? 1 : 0)}>
                    <EmptyState
                      icon={emptyIcon}
                      title={emptyTitle}
                      description={emptyDescription}
                      actionLabel={addNewLabel}
                      onAction={onAddNew}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && filteredData.length > itemsPerPage && (
          <div className="px-5 py-3.5 border-t border-slate-800 flex items-center justify-between bg-slate-950/40 text-xs text-slate-400">
            <div>
              Showing <span className="font-semibold text-white">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
              <span className="font-semibold text-white">
                {Math.min(currentPage * itemsPerPage, filteredData.length)}
              </span>{' '}
              of <span className="font-semibold text-white">{filteredData.length}</span> entries
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded-lg border border-slate-800 hover:bg-slate-800 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-slate-300 font-semibold px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-1.5 rounded-lg border border-slate-800 hover:bg-slate-800 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
